/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import connect from '../../../utils/connect';

import { FiltersFormContext } from './FiltersFormContext';

import { useFormikContext } from 'formik';

import { Typography, Flex, Select, TreeSelect } from '@app/ui';

import InputTag from './InputTag';

const { SHOW_PARENT } = TreeSelect;

const InsightsFiltersRow = () => {
    const { t } = useTranslation();
    const { values, setFieldValue } = useFormikContext();

    const {
        platformsOptions,
        accTypesOptions,
        mediaTypesOptions,
        collectionsOptions,
        setFormValue,
        hasPremiumCollections,
        hasInternationalBroadcastView,
        countryOptions,
        isCountrySelectionDisabled,
    } = useContext(FiltersFormContext);

    const get = key =>
        Object.entries(values[key] ?? {})
            .filter(([, value]) => value)
            .map(([key]) => `${key}`);

    const update = key => v => {
        setFieldValue(
            key,
            v.reduce((acc, value) => {
                acc[value] = true;
                return acc;
            }, {}),
            false,
        );
    };

    const ptfOptions = React.useMemo(() => {
        return platformsOptions.map(option => {
            const newNestedOptions =
                option.nested?.map(nestedOption => {
                    return {
                        value: `${option.value}-${nestedOption.value}`,
                        title: nestedOption.label,
                    };
                }) ?? null;

            return {
                value: `${option.value}`,
                title: option.label,
                children: newNestedOptions,
            };
        });
    }, [platformsOptions]);

    const dropdownWithSelectAll = (selectAll, hasOptions) => menu => {
        return (
            <>
                {hasOptions && (
                    <div
                        style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                        }}
                        onClick={selectAll}
                    >
                        <Typography.Text
                            style={{
                                fontWeight: 'bold', // Make it bold
                            }}
                        >
                            {t('filters.selectAll')}
                        </Typography.Text>
                    </div>
                )}
                {menu}
            </>
        );
    };

    return (
        <Flex
            vertical
            gap={8}
            style={{
                padding: '16px 0',
            }}
        >
            <Flex gap={16} wrap="wrap">
                {hasInternationalBroadcastView && (
                    <Flex vertical flex={1} gap={4}>
                        <Typography.Text
                            className="-filter-title"
                            data-test="new-plt-dropdown"
                        >
                            {t('filters.broadcastCountry')}
                        </Typography.Text>

                        <Select
                            mode="multiple"
                            placeholder={t('filters.selectBroadcastCountry')}
                            autoClearSearchValue={false}
                            options={countryOptions}
                            value={get('countries')}
                            onChange={update('countries')}
                            optionFilterProp="label"
                            allowClear
                            maxTagCount={2}
                            maxTagPlaceholder={`...`}
                            style={{
                                maxHeight: '32px',
                                overflow: 'hidden',
                            }}
                            disabled={
                                isCountrySelectionDisabled ||
                                countryOptions.length === 0
                            }
                            dropdownRender={dropdownWithSelectAll(() => {
                                update('countries')(
                                    countryOptions.map(({ value }) => value),
                                );
                            }, countryOptions.length > 0)}
                            className="-filter-select"
                        />
                    </Flex>
                )}

                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-plt-dropdown"
                    >
                        {t('filters.platforms')}
                    </Typography.Text>

                    <TreeSelect
                        treeCheckable
                        showCheckedStrategy={SHOW_PARENT}
                        placeholder={t('filters.selectPlatforms')}
                        autoClearSearchValue={false}
                        treeData={ptfOptions}
                        value={get('platforms')}
                        onChange={update('platforms')}
                        treeNodeFilterProp="title"
                        showSearch
                        allowClear
                        maxTagCount={2}
                        maxTagTextLength={8}
                        maxTagPlaceholder={`...`}
                        style={{
                            maxHeight: '32px',
                            overflow: 'hidden',
                        }}
                        dropdownRender={dropdownWithSelectAll(() => {
                            update('platforms')(
                                ptfOptions.map(({ value }) => value),
                            );
                        }, ptfOptions.length > 0)}
                        disabled={ptfOptions.length === 0}
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-plt-dropdown"
                    >
                        {t('filters.accountTypes')}
                    </Typography.Text>
                    <Select
                        mode="multiple"
                        placeholder={t('filters.selectAccountTypes')}
                        options={accTypesOptions}
                        autoClearSearchValue={false}
                        value={get('accTypes')}
                        onChange={update('accTypes')}
                        optionFilterProp="label"
                        allowClear
                        maxTagCount={1}
                        maxTagPlaceholder={`...`}
                        style={{
                            maxHeight: '32px',
                            overflow: 'hidden',
                        }}
                        dropdownRender={dropdownWithSelectAll(() => {
                            update('accTypes')(
                                accTypesOptions.map(({ value }) => value),
                            );
                        }, accTypesOptions.length > 0)}
                        disabled={accTypesOptions.length === 0}
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-mt-dropdown"
                    >
                        {t('filters.mediaTypes')}
                    </Typography.Text>
                    <Select
                        mode="multiple"
                        placeholder={t('filters.selectMediaTypes')}
                        options={mediaTypesOptions}
                        autoClearSearchValue={false}
                        value={values.mediaTypes}
                        onChange={setFormValue('mediaTypes')}
                        optionFilterProp="label"
                        allowClear
                        maxTagCount={1}
                        maxTagPlaceholder={`...`}
                        style={{
                            maxHeight: '32px',
                            overflow: 'hidden',
                        }}
                        dropdownRender={dropdownWithSelectAll(() => {
                            setFormValue('mediaTypes')(
                                mediaTypesOptions.map(({ value }) => value),
                            );
                        }, mediaTypesOptions.length > 0)}
                        disabled={mediaTypesOptions.length === 0}
                    />
                </Flex>
            </Flex>
            <Flex gap={16}>
                {hasPremiumCollections && (
                    <Flex vertical flex={1} gap={4}>
                        <Typography.Text
                            className="-filter-title"
                            data-test="new-c-dropdown"
                        >
                            {t('filters.collections')}
                        </Typography.Text>
                        <Select
                            placeholder={t('filters.selectCollections')}
                            mode="multiple"
                            options={collectionsOptions}
                            value={values.collections}
                            onChange={setFormValue('collections')}
                            optionFilterProp="label"
                            allowClear
                            maxTagCount={1}
                            maxTagPlaceholder={`...`}
                            style={{
                                maxHeight: '32px',
                                overflow: 'hidden',
                            }}
                            autoClearSearchValue={false}
                            dropdownRender={dropdownWithSelectAll(() => {
                                setFormValue('collections')(
                                    collectionsOptions.map(
                                        ({ value }) => value,
                                    ),
                                );
                            }, collectionsOptions.length > 0)}
                            disabled={collectionsOptions.length === 0}
                        />
                    </Flex>
                )}
                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-ht-dropdown"
                    >
                        {t('filters.hashtags')}
                    </Typography.Text>
                    <InputTag
                        value={values.hashtags}
                        onChange={setFormValue('hashtags')}
                        placeholderText={t('filters.enterHashtags')}
                        data-test="new-ht-dropdown"
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-hd-dropdown"
                    >
                        {t('filters.handles')}
                    </Typography.Text>
                    <InputTag
                        value={values.handles}
                        onChange={setFormValue('handles')}
                        placeholderText={t('filters.enterHandles')}
                        isHandleInput
                    />
                </Flex>
            </Flex>
        </Flex>
    );
};

export default connect(['filters'])(InsightsFiltersRow);
